import React, { useState}from 'react';
import { Tooltip } from 'react-tooltip';
import { Info, BarChart2, ArrowUp, ArrowDown, ArrowLeftRight, Copy, Check } from 'lucide-react';

const formatNumber = (num) => new Intl.NumberFormat().format(num);

const getPriceDiffInfo = (current, average) => {
  if (!average || average === 0) return { percent: 0, color: 'text-gray-400' };
  const diffPercent = ((current - average) / average) * 100;
  const color = Math.abs(diffPercent) <= 2 
    ? 'text-gray-400' 
    : diffPercent > 0 
      ? 'text-red-500'
      : 'text-green-500';
  return { percent: diffPercent, color };
};

const ItemImage = ({ itemId, name }) => {
  const [imageLoaded, setImageLoaded] = React.useState(false);

  const handleImageLoad = React.useCallback(() => {
    setImageLoaded(true);
  }, []);

  return (
    <div className="item_icon" style={{ position: 'relative' }}>
      {!imageLoaded && (
        <img
          className="item_icon"
          src="/items.png"
          alt="Item placeholder"
          style={{ position: 'absolute' }}
        />
      )}
      <img 
        className="item_icon"
        style={{ opacity: imageLoaded ? 1 : 0, transition: 'opacity 0.2s' }}
        src={`https://imageserver.eveonline.com/Type/${itemId}_64.png`}
        alt={name || 'Item icon'}
        loading="lazy"
        onLoad={handleImageLoad}
      />
    </div>
  );
};

const OptimizerResults = ({ results, onIgnoreListChange, initialIgnoreList = ''  }) => {
  const [ignoreOre, setIgnoreOre] = useState(initialIgnoreList);
  const [tooltipText, setTooltipText] = useState('');
  const [tooltipId, setTooltipId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

    const copyOre = () => {
    const materialsCopyText = results.ores
      .map(ore => `${ore.name} x${formatNumber(ore.quantity)}`)
      .join('\n');
    
    navigator.clipboard.writeText(materialsCopyText).then(() => {
      setCopiedId('processed-materials');
      setTooltipText('Copied!');
      setTooltipId('processed-materials');
      setTimeout(() => {
        setCopiedId(null);
        setTooltipId(null);
      }, 1500);
    }).catch((err) => {
      console.error('Failed to copy text:', err);
    });
  };
  React.useEffect(() => {
    setIgnoreOre(initialIgnoreList);
  }, [initialIgnoreList]);

  const handleIgnoreListChange = (e) => {
    const value = e.target.value;
    setIgnoreOre(value);
    // Call the callback function to pass the ignore list to parent
    if (onIgnoreListChange) {
      onIgnoreListChange(value);
    }
  };

  if (!results) return null;

  // Split ignore list into array of ores, filtering out empty lines
  const ignoredOres = results.ignored_ores;

  return (
    <div className="optimizer-results mt-6 border border-neutral-600 rounded-lg bg-neutral-900 p-4">
      <h3 className="text-xl font-mono mb-4">Ore Optimization Results</h3>
      
      <div className="grand-totals grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="total-item">
          <strong className>Ore Cost</strong>
          <span>{formatNumber(results.summary?.total_ore_cost)} ISK</span>
        </div>
        <div className="total-item">
          <strong>Mineral Cost</strong>
          <span>{formatNumber(results.summary?.total_mineral_value)} ISK</span>
        </div>
        <div className="total-item">
          <strong>Profit Margin</strong>
          <span> {formatNumber(results.summary?.net_profit)} ISK</span>
        </div>
        <div className="total-item">
          <strong>Required Minerals Value</strong>
          <span>{formatNumber(results.summary?.required_mineral_value)} ISK</span>
        </div>
        <div className="total-item">
          <strong>Surplus Minerals Value</strong>
          <span>{formatNumber(results.summary?.surplus_mineral_value)} ISK</span>
        </div>
        <div className="total-item">
          <strong>Ore Volume</strong>
          <span>{formatNumber(results.summary?.total_ore_volume)} m<sup>3</sup></span>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        {/* Ore Results Table */}
        <div className="flex-2 overflow-x-auto">
          <table className="results-table w-full">
            <thead>
              <tr className="bg-neutral-800 border-b border-neutral-700">
                <th className="p-3 text-center font-mono">
                  <button
                    className="p-2 size-10 rounded-lg font-medium bg-neutral-900 hover:bg-orange-400 transition-colors flex items-center gap-2 text-orange-300 hover:text-neutral-900"
                    onClick={copyOre ? copyOre : null}
                    data-tooltip-id={`${copyOre ? 'processed' : 'raw'}-materials-tooltip`}
                    data-tooltip-content={tooltipId === `${copyOre ? 'processed' : 'raw'}-materials` ? tooltipText : ''}
                  >
                    {copiedId === `${copyOre ? 'processed' : 'raw'}-materials` ? (
                      <Check size={24} />
                    ) : (
                      <Copy size={24} />
                    )}
                  </button>
                </th>
                <th className="p-3 text-center font-mono">Quantity</th>
                <th className="p-3 text-center font-mono">Cost</th>
                <th className="p-3 text-center font-mono">Mineral Value</th>
              </tr>
            </thead>
            <tbody>
              {results.ores?.map((ore, index) => {
                const sellDiff = getPriceDiffInfo(ore.sell_price, ore.sell_average);
                const buyDiff = getPriceDiffInfo(ore.buy_price, ore.buy_average);

                return (
                  <tr key={index} className="border-b border-neutral-700">
                    <td className="p-3 flex items-center gap-2">
                      <ItemImage itemId={ore.type_id} name={ore.name} />
                        {ore.name}
                    </td>
                    <td className="p-3 text-right">{formatNumber(ore.quantity)}</td>
                    <td className="p-3 text-right">{formatNumber(ore.cost)} ISK</td>
                    <td className="p-3 text-right">{formatNumber(ore.mineral_value)} ISK</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Ore Ignore List */}
        <div className="flex-1">
          {/* <h4 className="text-lg font -mono mb-3">Ore Ignore List</h4> */}
          <textarea
            value={ignoreOre}
            onChange={handleIgnoreListChange}
            placeholder={`Enter Ore that you want to ignore, one per line.\nExample:\n\nCompressed Dark Glitter\nCompressed Argil Kylixium\nCompressed Azure Plagioclase`}
            rows={10}
            cols={60}
            className="ignorearea w-full"
          />
        </div>
      </div>

      {/* Mineral Breakdown and Currently Ignored Ores */}
      <div className="mt-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Mineral Breakdown box */}
          <div className="flex-1 mb-5 border border-neutral-700 rounded-lg bg-neutral-800 p-4">
            <h4 className="text-lg font-mono mb-3">Mineral Breakdown</h4>
            <div className="overflow-x-auto">
              <table className="results-table w-full">
                <thead>
                  <tr className="bg-neutral-800 border-b border-neutral-700">
                    <th className="p-3 text-left font-mono">Mineral</th>
                    <th className="p-3 text-right font-mono">Required</th>
                    <th className="p-3 text-right font-mono">Produced</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(results.mineral_breakdown || {}).map(([mineralId, data]) => (
                    <tr key={mineralId} className="border-b border-neutral-700">
                      <td className="p-3 flex items-center gap-2">
                        <ItemImage itemId={mineralId} name={data.name} />
                          {data.name}
                      </td>
                      <td className="p-3 text-right">{formatNumber(data.required)}</td>
                      <td className="p-3 text-right">{formatNumber(data.produced)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Currently Ignored Ores box */}
          <div className="flex-1 mb-5 border border-neutral-700 rounded-lg font-mono bg-neutral-800 p-4">
            <h4 className="text-lg font-mono mb-3">Currently Ignored Ores</h4>
            {ignoredOres.length > 0 ? (
              <ul className="space-y-2 border border-neutral-600 rounded-lg p-3">
                {ignoredOres.map((ore, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <span className="text-neutral-300 font-mono">{ore}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-neutral-400 italic font-mono">No ores are being ignored</p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 text-sm text-gray-400">
        <p>Market: {results.market.toUpperCase()} | Approach: {results.approach.replace(/_/g, ' ')}</p>
        <p>Yield Factor: {(results.yield_factor * 100).toFixed(2)}% | Timestamp: {new Date(results.timestamp).toLocaleString()}</p>
      </div>
    </div>
  );
};

export default OptimizerResults;